import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';

const GuestReviews = ({ reviews = [] }) => {
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all');
  const [showAll, setShowAll] = useState(false);

  const defaultReviews = [
    {
      id: 1,
      guestName: "Priya Sharma",
      location: "Mumbai, India",
      rating: 5,
      date: "2025-09-10",
      title: "Perfect forest getaway!",
      review: `The Deluxe Forest Villa exceeded all our expectations. The location is absolutely stunning with panoramic forest views from the balcony. The room was spacious, clean, and well-appointed with modern amenities.\n\nThe staff was incredibly helpful and the complimentary welcome drinks were a nice touch. We especially loved waking up to the sounds of birds chirping. Highly recommend for nature lovers!`,
      helpful: 12,
      verified: true,
      photos: [
        "https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg",
        "https://images.pexels.com/photos/271618/pexels-photo-271618.jpeg"
      ]
    },
    {
      id: 2,
      guestName: "Rajesh Kumar",
      location: "Bangalore, India",
      rating: 4,
      date: "2025-09-05",
      title: "Great experience with minor issues",
      review: `Overall a wonderful stay at Dandeli Adventure Resorts. The villa was comfortable and the forest setting is magical. The Wi-Fi was a bit slow, but honestly, it was nice to disconnect and enjoy nature.\n\nThe adventure activities were well-organized and the food was delicious. Would definitely come back with family.`,
      helpful: 8,
      verified: true,
      photos: []
    },
    {
      id: 3,
      guestName: "Sarah Johnson",
      location: "London, UK",
      rating: 5,
      date: "2025-08-28",
      title: "Incredible wildlife experience",
      review: `As international tourists, we were amazed by the authentic Indian wilderness experience. The villa provided the perfect base for exploring Dandeli Wildlife Sanctuary.\n\nThe staff went above and beyond to make our stay comfortable. The room service was prompt and the local cuisine recommendations were spot on. This place is a hidden gem!`,
      helpful: 15,
      verified: true,
      photos: [
        "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg"
      ]
    },
    {
      id: 4,
      guestName: "Amit Patel",
      location: "Ahmedabad, India",
      rating: 4,
      date: "2025-08-20",
      title: "Perfect for families",
      review: `Stayed here with my wife and two kids. The villa had plenty of space for all of us and the kids loved spotting different birds from the balcony.\n\nThe resort organized some great activities for the children. Only minor complaint is that the air conditioning could be a bit stronger during the day, but the natural breeze in the evening made up for it.`,
      helpful: 6,
      verified: true,
      photos: []
    },
    {
      id: 5,
      guestName: "Lisa Chen",
      location: "Singapore",
      rating: 5,
      date: "2025-08-15",
      title: "Romantic and peaceful",
      review: `My husband and I chose this for our anniversary trip and it was absolutely perfect. The villa is beautifully designed and the forest views are breathtaking.\n\nWe enjoyed the complimentary welcome drinks on the balcony while watching the sunset. The bed was incredibly comfortable and the bathroom amenities were top-notch. Highly recommended for couples!`,
      helpful: 10,
      verified: true,
      photos: [
        "https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg",
        "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg"
      ]
    }
  ];

  const reviewData = reviews?.length > 0 ? reviews : defaultReviews;

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'rating', label: 'Highest Rating' },
    { value: 'helpful', label: 'Most Helpful' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Reviews' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: 'photos', label: 'With Photos' }
  ];

  const sortedAndFilteredReviews = reviewData?.filter(review => {
      if (filterBy === 'all') return true;
      if (filterBy === 'photos') return review?.photos?.length > 0;
      return review?.rating === parseInt(filterBy);
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b?.rating - a?.rating;
        case 'helpful':
          return b?.helpful - a?.helpful;
        case 'recent':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

  const displayedReviews = showAll ? sortedAndFilteredReviews : sortedAndFilteredReviews?.slice(0, 3);

  const averageRating = reviewData?.reduce((sum, review) => sum + review?.rating, 0) / reviewData?.length;
  const ratingDistribution = [5, 4, 3, 2, 1]?.map(rating => ({
    rating,
    count: reviewData?.filter(review => review?.rating === rating)?.length,
    percentage: (reviewData?.filter(review => review?.rating === rating)?.length / reviewData?.length) * 100
  }));

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return [...Array(5)]?.map((_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? "text-accent fill-current" : "text-muted-foreground"}
      />
    ));
  };

  return (
    <div className="space-y-8">
      {/* Reviews Header */}
      <div className="border-b border-border pb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
          Guest Reviews
        </h2>
        
        {/* Rating Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Overall Rating */}
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-foreground mb-1">
                {averageRating?.toFixed(1)}
              </div>
              <div className="flex items-center justify-center mb-1">
                {renderStars(Math.round(averageRating))}
              </div>
              <div className="text-sm text-muted-foreground">
                Based on {reviewData?.length} reviews
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="space-y-2">
            {ratingDistribution?.map(({ rating, count, percentage }) => (
              <div key={rating} className="flex items-center space-x-3">
                <span className="text-sm font-medium text-foreground w-6">
                  {rating}
                </span>
                <Icon name="Star" size={14} className="text-accent fill-current" />
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className="bg-accent h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground w-8">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Filters and Sort */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          label="Sort by"
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          className="sm:w-48"
        />
        <Select
          label="Filter by"
          options={filterOptions}
          value={filterBy}
          onChange={setFilterBy}
          className="sm:w-48"
        />
      </div>
      {/* Reviews List */}
      <div className="space-y-6">
        {displayedReviews?.map((review) => (
          <div key={review?.id} className="bg-card border border-border rounded-lg p-6">
            {/* Review Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-semibold">
                    {review?.guestName?.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-foreground">
                      {review?.guestName}
                    </h4>
                    {review?.verified && (
                      <div className="flex items-center space-x-1 bg-success/10 px-2 py-1 rounded-full">
                        <Icon name="Shield" size={12} className="text-success" />
                        <span className="text-xs text-success font-medium">Verified</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={12} />
                    <span>{review?.location}</span>
                    <span>•</span>
                    <span>{formatDate(review?.date)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {renderStars(review?.rating)}
              </div>
            </div>

            {/* Review Content */}
            <div className="mb-4">
              <h5 className="font-semibold text-foreground mb-2">
                {review?.title}
              </h5>
              <div className="prose prose-gray max-w-none">
                {review?.review?.split('\n')?.map((paragraph, index) => (
                  <p key={index} className="text-muted-foreground leading-relaxed mb-3 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Review Photos */}
            {review?.photos?.length > 0 && (
              <div className="mb-4">
                <div className="flex space-x-2 overflow-x-auto">
                  {review?.photos?.map((photo, index) => (
                    <div key={index} className="flex-shrink-0">
                      <img
                        src={photo}
                        alt={`Review photo ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Review Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <button className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-micro">
                <Icon name="ThumbsUp" size={16} />
                <span className="text-sm">Helpful ({review?.helpful})</span>
              </button>
              <button className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-micro">
                <Icon name="Flag" size={16} />
                <span className="text-sm">Report</span>
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Show More Button */}
      {!showAll && sortedAndFilteredReviews?.length > 3 && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setShowAll(true)}
            iconName="ChevronDown"
            iconPosition="right"
          >
            Show {sortedAndFilteredReviews?.length - 3} more reviews
          </Button>
        </div>
      )}
    </div>
  );
};

export default GuestReviews;